import type { VoidFunctionComponent } from 'react';
import Subnav from '../../generic/Subnav';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import { getCategoryLabel } from '../../../helpers/issuesV2';
import { isFreeTier } from '../../../../shared/subscriptions';
import { issueOverviewFeature } from '../../settings/billing/features/consts';
import useProjectIssuesSubnav from './useProjectIssuesSubnav';
import { window } from '../../../globals';

type Props = {
  baseRevisionURL: string;
  revisionScanId?: number;
  projectLocator: string;
  revisionId: string | null;
};

/**
 * ProjectSubnavItemIssues is the Issues dropdown subnav item for projects.
 */
const ProjectSubnavItemIssues: VoidFunctionComponent<Props> = ({
  baseRevisionURL,
  revisionScanId,
  projectLocator,
  revisionId,
}) => {
  const {
    issueCount,
    categorySummary,
    totalIssuesCount,
    permittedCategories,
    getProjectIssuesURL,
  } = useProjectIssuesSubnav({
    baseRevisionURL,
    revisionScanId,
    projectLocator,
    revisionId,
  });

  const isIssueOverviewRestricted = isFreeTier(window.user?.organization?.stripe_plan);

  return (
    <Subnav.Dropdown
      to={`${baseRevisionURL}/issues`}
      navKey="issues"
    >
      <Subnav.Dropdown.RootItem>
        Issues
        <SubnavCountBadge count={issueCount || 0} isLoading={issueCount === null} />
      </Subnav.Dropdown.RootItem>
      <Subnav.Dropdown.PaidFeatureItem
        to={`${baseRevisionURL}/issues/overview`}
        navKey="issues/overview"
        isRestricted={isIssueOverviewRestricted}
        modalFocusFeature={issueOverviewFeature}
      >
        Overview
        {!isIssueOverviewRestricted && (
          <SubnavCountBadge count={totalIssuesCount || 0} isLoading={totalIssuesCount === null} />
        )}
      </Subnav.Dropdown.PaidFeatureItem>
      {permittedCategories.map(category => (
        <Subnav.Dropdown.Item
          key={category}
          to={getProjectIssuesURL(category)}
          navKey={`issues/${category}`}
        >
          {getCategoryLabel(category)}
          <SubnavCountBadge count={categorySummary?.[category] || 0} isLoading={!categorySummary} />
        </Subnav.Dropdown.Item>
      ))}
    </Subnav.Dropdown>
  );
};

export default ProjectSubnavItemIssues;
